import React, { useMemo, useContext, type FC, type PropsWithChildren } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { isCircuitOpen } from '@jira/platform__cookie-circuit-breaker/src/is-circuit-open.tsx';
import type { CookieCircuitBreakerConfig } from '@jira/platform__routing-route-types/src/JiraSpaRoute.tsx';
import {
	markSSRFailure,
	SSR_FALLBACK_MARKER_ATTRIBUTE,
} from '@jira/ssr__ssr-failure-tracker/src/tracker.tsx';
import { isStreamingEnabled } from '@jira/ssr__streaming-enabled/src/isStreamingEnabled.tsx';
import { detectionBoundary } from './detectionBoundary.tsx';
import { FallbackIndicator } from './FallbackIndicator.tsx';
import type { SSRFailureProps } from './SSRFailureProps.types.tsx';

const useCircuitBreakerConfigRouteSelector: () => CookieCircuitBreakerConfig | undefined =
	createRouterSelector((state) => state.route?.bifrost?.circuitBreakerConfig);
// eslint-disable-next-line camelcase
export { useCircuitBreakerConfigRouteSelector as useCircuitBreakerConfigRouteSelector__EXPORTED_FOR_TESTING };

/**
 * Reports a SSR failure on render. To be placed in (top level) Suspense fallback
 * 🚨Single use per route 🚨
 */
export const ReportSSRFailOnUse: FC<
	{
		ignoreWhenCircuitOpen?: boolean;
		/** ID of the current page, included in reported failure */
		pageId: string;
		/**
		 * @deprecated DO NOT USE unless you know what you are doing; reports failure even if DisableSSRFailDetection is in the tree above this.
		 * You might use this to indicate that a certain nested suspense boundary is critical to your page load, even if it is not the top-level one
		 */
		UNSAFE__forceReporting?: boolean;
	} & SSRFailureProps
> = ({
	ignoreWhenCircuitOpen,
	pageId,
	UNSAFE__forceReporting = false,
	reportToSlackChannel,
	fallbackId,
	displayErrorInStaging,
}) => {
	const circuitBreakerConfig = useCircuitBreakerConfigRouteSelector();
	const isCircuitOpenValue = isCircuitOpen(circuitBreakerConfig);
	const isCookieCircuitBreakerOpen =
		ignoreWhenCircuitOpen && isCircuitOpenValue && circuitBreakerConfig?.isCircuitBreakerEnabled;
	const isIgnored =
		isCookieCircuitBreakerOpen || isStreamingEnabled() || fg('jfp-magma-ssr-streaming-cleanup');

	const shouldReport = useContext(detectionBoundary) || UNSAFE__forceReporting;

	// oxlint-disable-next-line react/void-use-memo
	useMemo(() => {
		if (!__SERVER__) {
			// This component only reports in SSR
			return;
		}

		// do not report when cb is open and this failure is to do with the resources not being loaded.
		// However, still show circuit breaker message when appropriate
		// Also ignored in the streaming path - that needs to use <template> markers in the HTML rather
		// than running a side effect in `useMemo` (doesn't work under streaming/Suspense).
		if (isIgnored) {
			return;
		}

		if (shouldReport) {
			// oxlint-disable-next-line no-console
			console.error({
				error: `SSR ended rendering a fallback (${fallbackId}) on page ${pageId}`,
				message: 'Top level suspense boundary was triggered. This is not expected to happen',
				pageId,
			});
			markSSRFailure(fallbackId);
		} else {
			// oxlint-disable-next-line no-console
			console.warn(
				`SSR ended rendering a non-top-level fallback (${fallbackId}) on page ${pageId}`,
			);
		}
	}, [isIgnored, shouldReport, fallbackId, pageId]);

	if (
		(isStreamingEnabled() || fg('jfp-magma-ssr-streaming-cleanup')) &&
		!isCookieCircuitBreakerOpen
	) {
		// Under streaming a fallback render is not terminal, so the legacy `useMemo` side-effect above is disabled;
		// instead emit an inert marker that the streaming renderer (`markSsrFailuresFromHtml`) correlates with React's
		// boundary-completion (`$RC`) scripts to decide whether this fallback was the terminal SSR output.
		// Same skip semantics as the legacy path - only when detection is active for this boundary.
		if (shouldReport && fg('jfp-magma-ssr-streaming-monitoring-fixes')) {
			return <template {...{ [SSR_FALLBACK_MARKER_ATTRIBUTE]: fallbackId ?? '' }} />;
		}
		return null;
	}

	return (
		<FallbackIndicator
			reportToSlackChannel={reportToSlackChannel}
			fallbackId={fallbackId}
			displayErrorInStaging={displayErrorInStaging}
			isCookieCircuitBreakerFailure={isCookieCircuitBreakerOpen}
			cookieKey={circuitBreakerConfig?.cookieKey}
		/>
	);
};

/**
 * Disables SSR render failure detection.
 * To be placed inside Suspense to aid `RLL ssr:false` components
 */
export const DisableSSRFailDetection: FC<PropsWithChildren> = ({ children }) =>
	__SERVER__ ? (
		<detectionBoundary.Provider value={false}>{children}</detectionBoundary.Provider>
	) : (
		<>{children}</>
	);
